import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { Disabled } from "@insite/mobius/Swatch";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Search from "@tecumseh/MobiusOverrides/Icons/Search";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import { typography } from "@tecumseh/theme/styleguide";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { createRef, FC, useCallback, useEffect, useRef, useState } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import * as cssLinter from "css";
import PlayIconOutlined from "@tecumseh/Icons/PlayIconOutlined";

const enum isImageVideo {
    video = "video",
    image = "image",
}

interface CustomButtonModel {
    fields: {
        openInNewTab: boolean;
        overriddenTitle: string;
        linktype: "link" | "file" | "video";
        destination: LinkFieldValue;
        file: string;
        video: string;
    };
}

interface SlideshowList {
    fields: {
        isImageVideo: isImageVideo;
        title: string;
        image: string;
        description: string;
        blurEffect: string;
        gradientLevel: string;
        buttons: CustomButtonModel[];
    };
}

const enum fields {
    slideShows = "slideShows",
    customCSS = "customCSS",
    seconds = "seconds",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.slideShows]: SlideshowList[];
        [fields.customCSS]: string;
        [fields.seconds]: string;
    };
}
type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface Imgstyle {
    container?: InjectableCss;
    parent?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    buttonPREV?: InjectableCss;
    main?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    buttonNEXT?: InjectableCss;
    title?: TypographyProps;
    parentContainer?: InjectableCss;
    mainContainer?: InjectableCss;

    // Video Background Styles
    iframeBackgroundVideo?: InjectableCss;
    bannerTextContainer?: InjectableCss;
    bannerTitle?: InjectableCss;
    bannerDescription?: InjectableCss;
    buttonContainer?: InjectableCss;
    wrapper?: InjectableCss;
    videoContainer?: InjectableCss;
    ContentImg?: InjectableCss;
    btnLink?: LinkPresentationProps;
    btnFileLink?: LinkPresentationProps;
}

export const StyledWrapperContainer = styled.div<{ blur?: number, gradientLevel?: number }>`
    min-height: 700px;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    z-index: 1;
    justify-content: center;
    position: relative;
    
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                     min-height: 60vh;
                     padding: 0px;
                `,
                null,
                css`
                    padding: 0px 76px;
                `,
                css`
                    min-height: 700px;
                    padding: 0px 100px;
                `,
                css`
                    padding: 0px 60px;
                `
            ],
            "min",
        )};

    &::after {
        content: "";
        height: 100%;
        width: 100%;
        position: absolute;
        backdrop-filter: ${({ blur }) => (blur ? `blur(${blur}px)` : "blur(0px)")};
        background: ${({ gradientLevel = 82 }) => `
            linear-gradient(91deg,
            rgb(0 0 0 / ${gradientLevel}%) 0%,
            rgb(0 0 0 / ${gradientLevel / 2}%) 50%,
            rgb(26 26 26 / ${gradientLevel * 0.8}%) 100%
            )
        `};
        z-index: -1;
        left: 0px;
        right: 0px;
    }
`;

export const imgstyle: Imgstyle = {
    ContentImg: {
        css: css`
            width: 100%;
            top: 0px;
            left: 0;
            z-index: 1;
            height: 100%;
            position: absolute;
            object-fit: cover;
            img {
                object-fit: cover;
                aspect-ratio: 3.6/2.2 !important;
                height: 100%;
            }
        `,
    },
    buttonPREV: {
        css: css`
            /* background-color: ${COLORS.background.secondaryNavyThree}; */
            background-color: transparent;
            padding: 0;
            border-radius: 50%;
            height: 50px;
            width: 50px;
            border: none;
            svg {
                color: white;
            }
            &:hover {
                opacity: 1;
                background-color: ${getColor("secondary")};
            }
            &:disabled,
            button[disabled] {
                background-color: ${COLORS.background.darkGray};
                color: ${COLORS.dimGray};
            }
        `,
    },
    buttonNEXT: {
        css: css`
            background-color: transparent;
            padding: 0;
            border-radius: 50%;
            height: 50px;
            width: 50px;
            border: none;
            svg {
                color: white;
            }
            &:hover {
                opacity: 1;
                background-color: ${getColor("secondary")};
            }
            &:disabled,
            button[disabled] {
                background-color: ${COLORS.background.darkGray};
                color: ${COLORS.dimGray};
            }
        `,
    },
    title: {
        variant: "h2",
        css: css`
            opacity: 1;
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            /* font-size: ${TYPOGRAPHY.size.mLarge}; */
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallish};
            text-align: center;
            padding-top: 21px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.largest};
                        margin-left: 80px;
                        margin-right: 80px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])};
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            left: 3%;
            transform: translate(-50%, -50%);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        left: 45px;
                    `,
                    css`
                        left: 45px;
                    `,
                    css`
                        left: 45px;
                    `,

                    null,
                ])};
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            right: 1%;
            transform: translate(-50%, -50%);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        right: 0px;
                    `,
                    css`
                        right: 0px;
                    `,
                    css`
                        right: 0px;
                    `,

                    null,
                ])};
        `,
    },
    parentContainer: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    parent: {
        css: css`
            display: flex;
            margin-left: 0px;
            margin-right: 0px;
            gap: 30px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        gap: 25px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    container: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    mainContainer: {
        css: css`
            position: relative;
            padding: 0px;
            max-width: 100%;
            margin-left: auto;
            margin-right: auto;
        `,
    },
    main: {
        css: css`
            position: relative;
        `,
    },

    iframeBackgroundVideo: {
        css: css`
            top: 50%;
            left: 50%;
            min-width: 100%;
            min-height: 100%;
            width: auto;
            height: auto;
            object-fit: cover;
            pointer-events: none;
            transform: scale(1.5);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            transform: scale(2.5);
                        `,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    bannerTextContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    bannerTitle: {
        css: css`
            font-size: 3.4rem;
            color: ${COLORS.white};
            font-family: ${TYPOGRAPHY.fontFamily.montserrat};
            font-weight: ${TYPOGRAPHY.weight.bold};
            margin: 0 !important;
            line-height: 60px;
            margin-bottom: 20px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        margin-bottom: 10px;
                    `,
                    css`
                        width: 100%;
                        margin-bottom: 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    bannerDescription: {
        css: css`
            > p {
                color: ${COLORS.white};
                font-size: ${TYPOGRAPHY.size.base};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: ${TYPOGRAPHY.size.base};
                            width: 100%;
                            margin-top: 10px;
                        `,
                        css`
                            font-size: ${TYPOGRAPHY.size.larger};
                            width: 100%;
                            margin-top: 10px;
                        `,
                        css`
                            width: 100%;
                            font-size: ${TYPOGRAPHY.size.larger};
                            margin-top: 10px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            flex-wrap: wrap;
            gap: 15px;
            width: 100%;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            gap: 15px;
                            display: flex;
                            flex-wrap: wrap;
                        `,
                        css`
                            gap: 15px;
                            display: flex;
                            flex-wrap: wrap;
                        `,
                        css`
                            gap: 15px;
                            display: flex;
                            flex-wrap: wrap;
                        `,
                        css``,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null], "minMax")};
            button,
            a {
                background-color: #f5f9fd;
                padding: 10px 20px;
                border-radius: 25px;
                :hover {
                    opacity: 0.8;
                    background-color: #13ea9d;
                }
            }
            button {
                > span {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 5px;
                    svg {
                        height: 19px;
                    }
                }
            }
            span {
                display: flex;
                align-items: center;
                color: #092637 !important;
                font-size: ${TYPOGRAPHY.size.base};
                font-weight: ${TYPOGRAPHY.weight.baseBold} !important;
                font-family: ${TYPOGRAPHY.fontFamily.montserrat};
                border-radius: 25px;
                &:hover {
                    background-color: #13ea9d;
                }
            }
        `,
    },
    btnLink: {
        css: css`
            > span {
                :hover {
                    opacity: 0.8;
                }
            }
        `,
    },
    btnFileLink: {
        css: css`
            > button,
            a {
                :hover {
                  
                }
            }
            > span {
            }
        `,
    },
    videoContainer: {
        css: css`
            background: #000000db;
            z-index: 99;
            position: fixed;
            width: 100%;
            height: 100vh;
            top: 0px;
            left: 0px;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
};

const styles = imgstyle;

// Helper to extract video ID from any YouTube URL
function getYouTubeVideoId(url: string): string | null {
    try {
        const parsed = new URL(url);
        if (parsed.hostname === "youtu.be") {
            return parsed.pathname.slice(1);
        }
        if (parsed.hostname.includes("youtube.com")) {
            return parsed.searchParams.get("v");
        }
    } catch (e) {}
    return null;
}

declare global {
    interface Window {
        YT?: any;
        onYouTubeIframeAPIReady?: () => void;
    }
}

export const TECHomepageBannerSlideshow: FC<Props> = ({ fields, theme, shellContext }) => {
    // 🧩 Extract buttons from all slides
    const extractButtons = fields.slideShows?.flatMap(s => s.fields.buttons || []) || [];
    const buttons = useGetLinks(extractButtons, o => o.fields.destination);

    // 🎠 Embla setup (looping carousel)
    const [emblaRef, embla] = useEmblaCarousel({ loop: true });

    // 🪟 Track resize for responsive recalculations
    const [windowResizeTime, setWindowResizeTime] = useState(0);

    // ⬅️➡️ Scroll control
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    // 🎥 Video-related state/refs
    const [currentVideo, setCurrentVideo] = useState<{ slideName: string; showVideo: string } | null>(null);
    const videoRef = useRef<(HTMLVideoElement | null)[]>([]);
    const playerRef = useRef<(HTMLDivElement | null)[]>([]);
    const ytPlayerInstance = useRef<any[]>([]);
    const autoplayInterval = useRef<ReturnType<typeof setInterval> | null>(null);

    // 🎨 Slide wrapper styling
    const bannerWrapper = {
        css: css`
            padding: 0;
            max-width: 100%;
            position: relative;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [css``, css``, null, null, null])}
            ${fields.customCSS}
        `,
    };

    /**
     * 🎬 Load & initialize YouTube videos
     * Creates YT Player instances for any YouTube slides.
     */
    useEffect(() => {
        const slides = fields.slideShows;
        if (!slides?.length) return;

        // Filter only YouTube slides
        const youtubeSlides = slides
            .map((s, i) => ({ url: s.fields.image, index: i }))
            .filter(s => s.url && (s.url.includes("youtube.com") || s.url.includes("youtu.be")));

        if (!youtubeSlides.length) return;

        const createPlayer = (id: string, index: number) => {
            const el = playerRef.current[index];
            if (!el || ytPlayerInstance.current[index]) return;

            ytPlayerInstance.current[index] = new window.YT.Player(el, {
                videoId: id,
                playerVars: {
                    autoplay: 1,
                    mute: 1,
                    controls: 0,
                    playsinline: 1,
                    loop: 1,
                    playlist: id,
                    modestbranding: 1,
                    rel: 0,
                },
                events: {
                    onReady: (event: any) => {
                        event.target.mute();
                        event.target.playVideo();
                    },
                },
            });
        };

        const onYouTubeApiReady = () => {
            youtubeSlides.forEach(s => {
                const id = getYouTubeVideoId(s.url);
                if (id) createPlayer(id, s.index);
            });
        };

        // 🧩 Load YT API only once
        if (!window.YT) {
            window.onYouTubeIframeAPIReady = onYouTubeApiReady;
            const script = document.createElement("script");
            script.src = "https://www.youtube.com/iframe_api";
            document.body.appendChild(script);
        } else {
            onYouTubeApiReady();
        }

        // 🧹 Cleanup all players on unmount
        return () => {
            ytPlayerInstance.current.forEach((inst, i) => {
                if (inst?.destroy) {
                    inst.destroy();
                    ytPlayerInstance.current[i] = null;
                }
            });
        };
    }, [fields.slideShows]);

    /**
     * 🧭 Set scroll navigation state
     */
    const updateScrollButtons = useCallback(() => {
        if (shellContext.isInShell || !embla) return;
        setCanScrollPrev(embla.canScrollPrev());
        setCanScrollNext(embla.canScrollNext());
    }, [embla, shellContext.isInShell]);

    /**
     * 📱 Responsiveness: draggable mode and slides per scroll
     */
    const getDraggable = useCallback(
        () => (typeof window !== "undefined" ? window.innerWidth < theme.breakpoints.values[1] : false),
        [theme.breakpoints.values]
    );

    const [slidesToScroll, setSlidesToScroll] = useState(1);
    const [draggable, setDraggable] = useState(getDraggable());

    useEffect(() => {
        setSlidesToScroll(1);
        setDraggable(getDraggable());
    }, [windowResizeTime, getDraggable]);

    /**
     * 🔁 Embla initialization + autoplay setup
     */
    useEffect(() => {
        if (!embla) return;

        embla.on("init", updateScrollButtons);
        embla.on("select", updateScrollButtons);

        const delaySec = Number(fields?.seconds || 5);
        autoplayInterval.current = setInterval(() => embla?.scrollNext(), delaySec * 1000);

        const handleResize = () => setWindowResizeTime(Date.now());
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            clearInterval(autoplayInterval.current!);
            autoplayInterval.current = null;
            window.removeEventListener("resize", handleResize);
        };
    }, [embla, fields?.seconds, updateScrollButtons]);

    /**
     * ♻️ Reinitialize carousel when slides change or layout updates
     */
    useEffect(() => {
        if (!embla || !fields.slideShows?.length) return;

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
            loop: true,
        });
        updateScrollButtons();
    }, [embla, slidesToScroll, draggable, fields.slideShows, updateScrollButtons]);

    /**
     * ▶️ Autoplay local videos
     */
    useEffect(() => {
        fields.slideShows?.forEach((slide, i) => {
            const video = videoRef.current[i];
            if (video && !slide.fields.image.match(/youtube|youtu\.be/)) {
                video.play().catch(() => {});
            }
        });
    }, [fields.slideShows]);

    // 🛑 No slides to show
    if (!fields.slideShows?.length) return null;

    // ===========================================================
    // 🖼️ Render Section
    // ===========================================================
    return (
        <StyledWrapper
            css={css`
                opacity: 1;
                width: 100%;
                margin: auto;
            `}
        >
            <StyledWrapper {...styles.main}>
                {/* ◀️ Prev Button */}
                {fields.slideShows.length > 1 && 
                    <>
                        <StyledWrapper {...styles.prevArrowWrapper}>
                            <Button
                                aria-label="Banner slider Previous button"
                                {...styles.buttonPREV}
                                onClick={() => embla?.scrollPrev()}
                                disabled={!canScrollPrev}
                            >
                                <ButtonIcon src={ChevronLeft} />
                            </Button>
                        </StyledWrapper>

                        {/* ▶️ Next Button */}
                        <StyledWrapper {...styles.nextArrowWrapper}>
                            <Button
                                aria-label="Banner slider Next button"
                                {...styles.buttonNEXT}
                                onClick={() => embla?.scrollNext()}
                                disabled={!canScrollNext}
                            >
                                <ButtonIcon src={ChevronRight} />
                            </Button>
                        </StyledWrapper>
                    </>
                }

                {/* 🧱 Main Container */}
                <StyledWrapper {...styles.mainContainer} data-selector="slider_heroBanner">
                    <StyledWrapper {...styles.container} ref={emblaRef}>
                        <StyledWrapper {...styles.parent}>
                            {fields.slideShows.map((slide, index) => (
                                <StyledWrapper
                                    {...styles.parentContainer}
                                    key={`slide_${index}`}
                                    data-selector={`slider_heroBanner_${index}`}
                                    style={{ width: `calc(100% / ${slidesToScroll})` }}
                                >
                                    {/* 🖼️ Slide Content */}
                                    <StyledWrapper className="hero-slider-banner-wrapper" {...bannerWrapper}>
                                        {/* 🎞️ Video/Image Handling */}
                                        {slide.fields.isImageVideo === "video" ? (
                                            slide.fields.image.includes("youtube.com") ||
                                            slide.fields.image.includes("youtu.be") ? (
                                                // YouTube
                                                <StyledWrapper
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: "100%",
                                                        overflow: "hidden",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <StyledWrapper
                                                        ref={el => {playerRef.current[index] = el}}
                                                        {...styles.iframeBackgroundVideo}
                                                    />
                                                </StyledWrapper>
                                            ) : slide.fields.image.includes("vimeo.com") ? (
                                                // Vimeo
                                                <iframe
                                                    src={`https://player.vimeo.com/video/${slide.fields.image.split("/").pop()}?autoplay=1&mute=1`}
                                                    width="640"
                                                    height="360"
                                                    frameBorder="0"
                                                    allow="autoplay; fullscreen; picture-in-picture"
                                                    allowFullScreen
                                                    title="Vimeo video player"
                                                />
                                            ) : (
                                                // Local Video
                                                <video
                                                    ref={el => { videoRef.current[index] = el; }}
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        zIndex: 1,
                                                    }}
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                    src={slide.fields.image}
                                                />
                                            )
                                        ) : (
                                            // 🖼️ Static Image
                                            <LazyImage {...styles.ContentImg} src={slide.fields.image} altText="Banner Image"/>
                                        )}

                                        {/* 📝 Text & Buttons */}
                                        <StyledWrapperContainer blur={+slide.fields.blurEffect} gradientLevel={+slide.fields.gradientLevel} id={`slide_${index}`}>
                                            <GridContainer
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "15px",
                                                }}
                                            >
                                                <GridItem
                                                    width={[12, 12, 10, 9, 6]}
                                                    style={{
                                                        flexDirection: "column",
                                                        paddingTop: "40px",
                                                    }}
                                                >
                                                    {/* Title */}
                                                    <StyledWrapper {...styles.bannerTextContainer}>
                                                        <StyledWrapper {...styles.bannerTitle}>
                                                            <Typography as="p">{slide.fields.title}</Typography>
                                                        </StyledWrapper>
                                                    </StyledWrapper>

                                                    {/* Description */}
                                                    <StyledWrapper {...styles.bannerDescription}>
                                                        {parse(slide.fields.description, parserOptions)}
                                                    </StyledWrapper>

                                                </GridItem>
                                                <GridItem  width={12} style={{paddingBottom: "40px"}} >
                                                    {/* Buttons */}
                                                    <StyledWrapper {...styles.buttonContainer}>
                                                        {slide.fields.buttons.map((btn, idx) => {
                                                            const btnFields = btn.fields;
                                                            const link = buttons.links[idx]?.url || "";
                                                            const href =
                                                                btnFields.linktype === "file" && btnFields.file
                                                                    ? btnFields.file
                                                                    : link;
                                                            return (
                                                                <StyledWrapper
                                                                    key={`slideBtn_${idx}`}
                                                                    className="links-wrapper"
                                                                    {...styles.wrapper}
                                                                >
                                                                    {btnFields.linktype === "link" && (
                                                                        <>
                                                                            {href === "" ? null : <Link
                                                                                    href={href}
                                                                                    target={
                                                                                        btnFields.openInNewTab ? "_blank" : ""
                                                                                    }
                                                                                    {...styles.btnLink}
                                                                                >
                                                                                    {btnFields.overriddenTitle}
                                                                                </Link>
                                                                            }
                                                                        </>
                                                                    )}

                                                                    {btnFields.linktype === "video" && (
                                                                        <Link
                                                                            onClick={() =>
                                                                                setCurrentVideo({
                                                                                    slideName: `slide_${index}`,
                                                                                    showVideo: btnFields.video || "",
                                                                                })
                                                                            }
                                                                        >
                                                                            <Icon src={PlayIconOutlined} />
                                                                            {btnFields.overriddenTitle}
                                                                        </Link>
                                                                    )}

                                                                    {btnFields.linktype === "file" && (
                                                                        <Link
                                                                            {...styles.btnFileLink}
                                                                            onClick={() => {
                                                                                const anchor = document.createElement("a");
                                                                                anchor.href = btnFields.file;
                                                                                anchor.download = "";
                                                                                document.body.appendChild(anchor);
                                                                                anchor.click();
                                                                                document.body.removeChild(anchor);
                                                                            }}
                                                                        >
                                                                            {btnFields.overriddenTitle}
                                                                        </Link>
                                                                    )}
                                                                </StyledWrapper>
                                                            );
                                                        })}
                                                    </StyledWrapper>
                                                </GridItem>
                                            </GridContainer>
                                        </StyledWrapperContainer>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {/* 🎬 Overlay Video Player */}
            {currentVideo && (
                <StyledWrapper
                    {...styles.videoContainer}
                    onClick={() => setCurrentVideo(null)}
                >
                    <StyledWrapper onClick={e => e.stopPropagation()}>
                        {currentVideo.showVideo.match(/youtube|youtu\.be/) ? (
                            <iframe
                                width="640"
                                height="360"
                                src={`https://www.youtube.com/embed/${
                                    currentVideo.showVideo.includes("v=")
                                        ? new URL(currentVideo.showVideo).searchParams.get("v")
                                        : currentVideo.showVideo.split("/").pop()
                                }?autoplay=1&mute=1`}
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                            />
                        ) : currentVideo.showVideo.includes("vimeo.com") ? (
                            <iframe
                                src={`https://player.vimeo.com/video/${currentVideo.showVideo.split("/").pop()}?autoplay=1&mute=1`}
                                width="640"
                                height="360"
                                frameBorder="0"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <video
                                style={{ maxWidth: "640px", width: "100%" }}
                                height="360"
                                autoPlay
                                controls
                            >
                                <source src={currentVideo.showVideo} />
                            </video>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};


const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `.hero-slider-banner-wrapper {

}`;

const widgetModule: WidgetModule = {
    component: withIsInShell(withTheme(TECHomepageBannerSlideshow)),
    definition: {
        group: "Basic",
        displayName: "TEC Hero Banner Slideshow",
        fieldDefinitions: [
            {
                name: fields.slideShows,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { title } }) => title || "title",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "isImageVideo",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "image",
                        displayName: "Select Media Type",
                        isRequired: true,
                        options: [
                            {
                                displayName: "video",
                                value: isImageVideo.video,
                            },
                            {
                                displayName: "image",
                                value: isImageVideo.image,
                            },
                        ],
                        tab: basicTab,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        displayName: "Background Video/Image",
                        tab: basicTab,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: "blurEffect",
                        editorTemplate: "TextField",
                        defaultValue: "20",
                        tab: basicTab,
                    },
                    {
                        name: "gradientLevel",
                        displayName: "Overlay color opacity",
                        editorTemplate: "TextField",
                        defaultValue: "82",
                        tab: basicTab,
                    },
                    {
                        name: "buttons",
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields, state) => {
                            const {
                                destination: { type, value },
                                overriddenTitle,
                            } = item.fields;
                            if (overriddenTitle) {
                                return overriddenTitle;
                            }
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return link?.title;
                            }
                            if (type === "Category") {
                                let displayValue = "";
                                const categories = (state as any).pageEditor.categories;
                                if (!categories) {
                                    displayValue = "Loading...";
                                } else {
                                    const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                                    if (matchingCategories.length === 0) {
                                        displayValue = "Unknown";
                                    }
                                }

                                return displayValue;
                            }
                            if (type === "Url") {
                                return value;
                            }
                            return value;
                        },
                        fieldDefinitions: [
                            {
                                name: "linktype",
                                displayName: "Button Type",
                                editorTemplate: "DropDownField",
                                options: [
                                    { displayName: "Link", value: "link" },
                                    { displayName: "Video", value: "video" },
                                    { displayName: "File", value: "file" },
                                ],
                                defaultValue: "link",
                                hideEmptyOption: true,
                            },
                            {
                                name: "destination",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Page",
                                },
                                isVisible: item => item.fields.linktype === "link",
                            },
                            {
                                name: "file",
                                editorTemplate: "ImagePickerField",
                                defaultValue: "",
                                isVisible: item => item.fields.linktype === "file",
                            },
                            {
                                name: "video",
                                editorTemplate: "ImagePickerField",
                                defaultValue: "",
                                isVisible: item => item.fields.linktype === "video",
                            },
                            {
                                name: "overriddenTitle",
                                displayName: "Title",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "openInNewTab",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                        tab: basicTab,
                    },
                ],
            },
            {
                name: fields.seconds,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                displayName: "Number of Seconds to change the slider Automatically",
                defaultValue: "",
                tab: basicTab,
            },   
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                tab: advancedTab,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
